/*
 * Description of the universal chess interface (UCI)  https://gist.github.com/aliostad/f4470274f39d29b788c1b09519e67372/
 */

import { invariant, raise } from "@banjoanton/utils";
import { createLogger } from "@chesspresso/common";
import { Chess } from "chess.js";
import { LRUCache } from "lru-cache";
import { type EngineModel } from "../models/engine-model";
import type { UniversalWorker } from "../models/universal-worker";
import { ChessUtil } from "../utils/chess-util";
import { EvaluationScoreUtil } from "../utils/evaluation-score-util";

export type EvaluationScore = { type: "cp"; score: number };
export type EvaluationMateScore = { type: "mate"; score: number };
export type EvaluationMessageScore = EvaluationScore | EvaluationMateScore;

export type EvaluatorEvaluationMessage = {
    /** Search depth reached by the engine (in plies). */
    depth: number;
    /** Selective search depth (optional). */
    selDepth?: number;
    /** MultiPV index (optional), for multi-variation analysis. */
    multiPv?: number;
    /** Evaluation score: centipawns or mate. */
    score: EvaluationMessageScore;
    /** Number of nodes searched (optional). */
    nodes?: number;
    /** Nodes per second (optional). */
    nps?: number;
    /** Tablebase hits (optional). */
    tbHits?: number;
    /** Time spent searching in milliseconds (optional). */
    time?: number; // milliseconds
    /** Principal variation (best line found). */
    pv: string; // principal variation
    /** Original UCI message */
    raw: string;
    type: "EVALUATION";
    engine: EngineModel;
};

export type EvaluatorBestMoveMessage = {
    bestMove: string;
    ponder?: string;
    raw: string;
    type: "BEST_MOVE";
    engine: EngineModel;
};

export type EvaluatorBaseMessage = {
    raw: string;
    engine: EngineModel;
    type: "BASE";
};

export type EvaluatorEngineMessage =
    | EvaluatorEvaluationMessage
    | EvaluatorBestMoveMessage
    | EvaluatorBaseMessage;

export type EvaluatePositionResponse = {
    bestMoveMessage: EvaluatorBestMoveMessage;
    evaluationMessage: EvaluatorEvaluationMessage;
};

export type EvaluatorMessageType = EvaluatorEngineMessage["type"];

export type EvaluatePositionMultiPVResponse = {
    bestMoveMessage: EvaluatorBestMoveMessage;
    evaluations: EvaluatorEvaluationMessage[];
};

type CachedPositionEvaluation = {
    response: EvaluatePositionResponse;
    actualDepth: number;
};

const createEvaluationCacheKey = (fen: string, engineModel: EngineModel) =>
    `${fen}-${engineModel}`;

const helperEngine = new Chess();

const logger = createLogger("evaluator-engine");

export class EvaluatorEngine {
    stockfish: UniversalWorker;
    engineKey: EngineModel;
    isReady: boolean;
    listeners: ((msg: EvaluatorEngineMessage) => void)[] = [];
    private evaluationCache: LRUCache<string, CachedPositionEvaluation>;

    constructor(key: EngineModel, worker: UniversalWorker) {
        this.engineKey = key;
        this.stockfish = worker;
        this.isReady = false;
        this.evaluationCache = new LRUCache<string, CachedPositionEvaluation>({
            max: 500,
        });
        this.stockfish.addEventListener("message", (e) => {
            // @ts-expect-error - fix this later with correct types for both engines
            const message = this.map(e);
            if (message) {
                this.listeners.forEach((cb) => cb(message));
            }
        });
        this.init();
    }

    private defaultMultiPv = 3;

    onMessage(callback: (msg: EvaluatorEngineMessage) => void) {
        this.listeners.push(callback);

        return () => {
            this.listeners = this.listeners.filter((cb) => cb !== callback);
        };
    }

    awaitReady(): Promise<void> {
        if (this.isReady) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.onReady(() => {
                resolve();
            });
        });
    }

    // eslint-disable-next-line complexity
    private map(e: MessageEvent<string>) {
        const uciMessage = e?.data ?? e;

        if (!uciMessage) {
            return undefined;
        }

        const isBestMove = uciMessage.startsWith("bestmove");

        if (isBestMove) {
            const haveNoBestMove = uciMessage.includes("bestmove (none)");
            const bestMove = uciMessage.match(/bestmove\s+(\S+)/)?.[1];
            const message: EvaluatorBestMoveMessage = {
                bestMove: haveNoBestMove
                    ? ""
                    : (bestMove ?? raise("No best move")),
                ponder: uciMessage.match(/ponder\s+(\S+)/)?.[1],
                raw: uciMessage,
                type: "BEST_MOVE",
                engine: this.engineKey,
            };

            return message;
        }

        const isEvaluation = uciMessage.startsWith("info");

        if (isEvaluation) {
            let score: EvaluationMessageScore;
            const mateMatch = uciMessage.match(/score\s+mate\s+(-?\d+)/);
            if (mateMatch) {
                score = { type: "mate", score: Number(mateMatch[1]) };
            } else {
                score = {
                    type: "cp",
                    score: Number(
                        uciMessage.match(/score\s+cp\s+(-?\d+)/)?.[1] ?? 0
                    ),
                };
            }
            const message: EvaluatorEvaluationMessage = {
                depth: Number(uciMessage.match(/depth\s+(\S+)/)?.[1] ?? 0),
                selDepth: Number(uciMessage.match(/seldepth\s+(\S+)/)?.[1]),
                multiPv: Number(uciMessage.match(/multipv\s+(\S+)/)?.[1]),
                score,
                nodes: Number(uciMessage.match(/nodes\s+(\S+)/)?.[1]),
                nps: Number(uciMessage.match(/nps\s+(\S+)/)?.[1]),
                tbHits: Number(uciMessage.match(/tbhits\s+(\S+)/)?.[1]),
                time: Number(uciMessage.match(/time\s+(\S+)/)?.[1]),
                pv: uciMessage.match(/pv\s+(.*)/)?.[1] ?? "",
                raw: uciMessage,
                type: "EVALUATION",
                engine: this.engineKey,
            };

            return message;
        }

        const message: EvaluatorBaseMessage = {
            raw: uciMessage,
            type: "BASE",
            engine: this.engineKey,
        };

        return message;
    }

    init() {
        this.stockfish.postMessage("uci");

        const off = this.onMessage(({ raw }) => {
            if (raw === "uciok") {
                this.stockfish.postMessage("setoption name MultiPV value 1");
                this.stockfish.postMessage(
                    "setoption name UCI_AnalyseMode value true"
                );
                this.stockfish.postMessage("isready");
            } else if (raw === "readyok") {
                this.isReady = true;
                off();
            }
        });
    }

    onReady(callback: () => void) {
        const removeListener = this.onMessage(({ raw }) => {
            if (raw === "readyok") {
                callback();
                removeListener();
            }
        });
    }

    async evaluatePosition(
        fen: string,
        depth = 12
    ): Promise<EvaluatePositionResponse> {
        const normalizedDepth = Math.min(24, depth);
        const key = createEvaluationCacheKey(fen, this.engineKey);
        const cached = this.evaluationCache.get(key);

        // Reuse if cached depth >= requested depth
        if (cached && cached.actualDepth >= normalizedDepth) {
            logger.debug(
                `Using cached evaluation for depth ${normalizedDepth}`
            );
            return cached.response;
        }

        // Evaluate and cache
        const response = await this._evaluatePositionDirect(
            fen,
            normalizedDepth
        );
        const actualDepth = response.evaluationMessage.depth;
        this.evaluationCache.set(key, { response, actualDepth });

        return response;
    }

    private async _evaluatePositionDirect(
        fen: string,
        depth: number
    ): Promise<EvaluatePositionResponse> {
        helperEngine.load(fen);
        const winner = ChessUtil.getWinner(helperEngine);
        const isWhiteTurn = helperEngine.turn() === "w";

        if (winner) {
            const response: EvaluatePositionResponse = {
                bestMoveMessage: {
                    bestMove: "",
                    ponder: "",
                    raw: "bestmove (none)",
                    type: "BEST_MOVE",
                    engine: this.engineKey,
                },
                evaluationMessage: {
                    depth: 0,
                    score: { score: 0, type: "mate" },
                    pv: "",
                    raw: "info score mate 100",
                    type: "EVALUATION",
                    engine: this.engineKey,
                },
            };

            return response;
        }

        let latestEvaluation: EvaluatorEvaluationMessage | undefined;

        return new Promise<EvaluatePositionResponse>((resolve) => {
            const removeListener = this.onMessage((msg) => {
                if (msg.type === "EVALUATION") {
                    latestEvaluation = msg;
                }
                if (msg.type === "BEST_MOVE") {
                    // best move is last message sent by engine
                    removeListener();
                    invariant(
                        latestEvaluation,
                        "Latest evaluation should not be undefined"
                    );

                    latestEvaluation.score =
                        EvaluationScoreUtil.toNormalizedMoveScore(
                            latestEvaluation.score,
                            isWhiteTurn
                        );

                    resolve({
                        bestMoveMessage: msg,
                        evaluationMessage: latestEvaluation,
                    });
                }
            });
            latestEvaluation = undefined;
            this.stockfish.postMessage(`position fen ${fen}`);
            this.stockfish.postMessage(`go depth ${depth}`);
        });
    }

    async evaluatePositionMultiPV(
        fen: string,
        depth = 12,
        k = this.defaultMultiPv
    ): Promise<EvaluatePositionMultiPVResponse> {
        helperEngine.load(fen);
        const winner = ChessUtil.getWinner(helperEngine);
        const isWhiteTurn = helperEngine.turn() === "w";

        if (winner) {
            return {
                bestMoveMessage: {
                    bestMove: "",
                    ponder: "",
                    raw: "bestmove (none)",
                    type: "BEST_MOVE",
                    engine: this.engineKey,
                },
                evaluations: [
                    {
                        depth: 0,
                        selDepth: 0,
                        multiPv: 1,
                        score: { type: "mate", score: 100 },
                        pv: "",
                        raw: "info score mate 100",
                        type: "EVALUATION",
                        engine: this.engineKey,
                    },
                ],
            };
        }

        this.stockfish.postMessage(`setoption name MultiPV value ${k}`);

        const latestByPv = new Map<number, EvaluatorEvaluationMessage>();

        return new Promise<EvaluatePositionMultiPVResponse>((resolve) => {
            const remove = this.onMessage((msg) => {
                if (msg.type === "EVALUATION") {
                    const variations = msg.multiPv ?? 0;
                    if (variations < 1 || variations > k) {
                        return;
                    }

                    // Only update if this depth is equal or better than what we have for this PV line
                    const existing = latestByPv.get(variations);
                    if (!existing || msg.depth >= existing.depth) {
                        latestByPv.set(variations, msg);
                    }
                } else if (msg.type === "BEST_MOVE") {
                    this.stockfish.postMessage(
                        "setoption name MultiPV value 1"
                    );

                    remove();

                    const ordered: EvaluatorEvaluationMessage[] = [];
                    for (let i = 1; i <= k; i++) {
                        const ev = latestByPv.get(i);
                        if (ev) {
                            ordered.push(ev);
                        }
                    }

                    ordered.forEach((ev) => {
                        ev.score = EvaluationScoreUtil.toNormalizedMoveScore(
                            ev.score,
                            isWhiteTurn
                        );
                    });

                    resolve({
                        bestMoveMessage: msg,
                        evaluations: ordered,
                    });
                }
            });
            this.stockfish.postMessage(`position fen ${fen}`);
            this.stockfish.postMessage(`go depth ${depth}`);
        });
    }

    stop() {
        this.stockfish.postMessage("stop"); // Run when searching takes too long time and stockfish will return you the bestmove of the deep it has reached
    }

    terminate() {
        logger.debug("Stopping evaluator engine");
        this.isReady = false;
        this.stockfish.postMessage("quit"); // Run this before chessboard unmounting.
        this.stockfish.terminate();
    }

    getEngineModel(): EngineModel {
        return this.engineKey;
    }

    getWorker(): UniversalWorker {
        return this.stockfish;
    }
}
